import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  BookOpen, Search, Printer, Receipt, Users, Scissors, Wrench,
  Landmark, Package, Warehouse, FileSearch, FileText, FileCheck,
  CreditCard, Building2, UserCog, Settings, LayoutDashboard,
} from 'lucide-react';

// ── Secciones del manual ──────────────────────────────────────────────────────
const ALL_SECTIONS = [
  // ── Todos los roles ──────────────────────────────────────────────────────
  {
    id: 'caja',
    icon: Landmark,
    title: 'Operaciones de Caja',
    roles: ['admin', 'vendedor'],
    color: 'text-amber-500',
    items: [
      {
        q: '¿Cómo abrir un turno de caja?',
        a: 'Ve a "Operaciones de Caja" en el menú. Haz clic en "Abrir turno". El sistema registrará la hora de inicio. Debes tener un turno abierto para poder facturar o crear pedidos.',
      },
      {
        q: '¿Cómo cerrar el turno?',
        a: 'En la misma pantalla de caja, haz clic en "Cerrar turno". Verás un resumen de las ventas del turno. Al confirmar, se enviará automáticamente un correo de resumen al administrador. Si tienes pedidos de confección con fecha de entrega vencida y pagados al 100%, el sistema mostrará un listado para que los marques como Entregados antes de cerrar. Puedes marcarlos o continuar sin hacerlo.',
      },
      {
        q: '¿Qué pasa si intento facturar sin caja abierta?',
        a: 'El sistema te mostrará una advertencia. En el POS los productos serán visibles para consulta pero no podrás cobrar. En Pedidos el botón "Nuevo Pedido" mostrará un aviso. Si tu usuario no requiere caja (configurable por el administrador), podrás operar normalmente.',
      },
      {
        q: '¿Puedo tener más de un turno abierto a la vez?',
        a: 'No. Solo puedes tener un turno abierto a la vez. Debes cerrar el turno actual antes de abrir uno nuevo.',
      },
    ],
  },
  {
    id: 'pos',
    icon: Receipt,
    title: 'Facturar (POS)',
    roles: ['admin', 'vendedor'],
    color: 'text-cyan-500',
    items: [
      {
        q: '¿Cómo buscar y agregar un producto?',
        a: 'En la pantalla de Facturar, usa el buscador de productos o navega por páginas. Haz clic en una variante (talla/color) para agregarla al carrito. Puedes ajustar la cantidad directamente en el carrito.',
      },
      {
        q: '¿Cómo seleccionar un cliente?',
        a: 'Escribe al menos 2 caracteres en el campo "Buscar cliente" para ver sugerencias. Selecciona el cliente de la lista. Si no existe, puedes continuar como "Consumidor Final".',
      },
      {
        q: '¿Cómo aplicar un descuento?',
        a: 'En el panel derecho encontrarás el campo de descuento. Ingresa el monto a descontar. El total se actualizará automáticamente.',
      },
      {
        q: '¿Cómo agregar un costo de envío?',
        a: 'Si el campo de envío está habilitado (activado desde Configuración → Impresión), aparecerá entre el ITBIS y el Total. Ingresa el monto del envío.',
      },
      {
        q: '¿Qué métodos de pago están disponibles?',
        a: 'Efectivo, Tarjeta, Transferencia, Efectivo + Tarjeta, Efectivo + Transferencia. El método "Crédito" está deshabilitado temporalmente.',
      },
      {
        q: '¿Cómo enviar el comprobante por correo?',
        a: 'Al completar la venta, aparecerá un modal de comprobante. Si el cliente tiene correo, se enviará automáticamente según el contador configurado. También puedes enviarlo manualmente haciendo clic en "Enviar por correo".',
      },
      {
        q: '¿Cómo facturar un pedido de confección?',
        a: 'Al crear un pedido de confección, el sistema te redirige automáticamente al POS con el monto del anticipo precargado. Selecciona el método de pago y completa la facturación normalmente. Si el pago cubre el 100% del pedido, al cerrar el comprobante el sistema te preguntará si el cliente recibió las prendas en ese momento. Si dices "Sí", el pedido se marca como Entregado automáticamente. Si dices "No, después", el pedido queda en su estado actual.',
      },
    ],
  },
  {
    id: 'clientes',
    icon: Users,
    title: 'Clientes',
    roles: ['admin', 'vendedor'],
    color: 'text-indigo-500',
    items: [
      {
        q: '¿Cómo crear un nuevo cliente?',
        a: 'Ve a "Clientes" y haz clic en "Nuevo Cliente". Completa nombre, teléfono (obligatorio) y correo. El teléfono debe ser único — si ya existe, el sistema te avisará pero puedes continuar si es correcto.',
      },
      {
        q: '¿Cómo buscar un cliente?',
        a: 'Usa el buscador en la parte superior. Puedes buscar por nombre, teléfono o correo electrónico. Los resultados aparecen con un pequeño retardo para no sobrecargar el sistema.',
      },
      {
        q: '¿Cómo editar la información de un cliente?',
        a: 'Haz clic sobre la tarjeta del cliente para abrir su detalle. Dentro del detalle encontrarás el botón "Editar". Realiza los cambios y guarda.',
      },
      {
        q: '¿Puedo ver el historial de compras de un cliente?',
        a: 'Sí. Dentro del detalle del cliente encontrarás su historial de facturas y pedidos.',
      },
      {
        q: '¿Cómo se ingresa el número de teléfono?',
        a: 'El campo de teléfono incluye un selector de bandera de país (🇩🇴 República Dominicana por defecto). Selecciona el país y escribe el número sin el código de país.',
      },
    ],
  },
  {
    id: 'tailoring',
    icon: Scissors,
    title: 'Pedidos de Confección',
    roles: ['admin', 'vendedor'],
    color: 'text-purple-500',
    items: [
      {
        q: '¿Cómo crear un nuevo pedido de confección?',
        a: 'Ve a "Pedidos Confección" y haz clic en el botón "+" del menú o en "Nuevo Pedido". Busca el cliente, agrega las prendas con sus medidas y cambios solicitados, define el anticipo y guarda. El sistema te llevará al POS para cobrar el anticipo.',
      },
      {
        q: '¿Cuál es el anticipo mínimo para crear un pedido?',
        a: 'El anticipo mínimo es el 15% del total del pedido. La producción inicia automáticamente cuando el cliente alcanza el 50% pagado.',
      },
      {
        q: '¿Cuáles son los estados de un pedido?',
        a: 'PENDIENTE_PAGO → PRE_PRODUCCIÓN → EN_PRODUCCIÓN → CONFECCIONADO → LISTO_PARA_ENTREGA → ENTREGADO. También puede pasar a CANCELADO.',
      },
      {
        q: '¿Cómo funciona el descuento por prenda?',
        a: 'Al agregar una prenda, puedes activar el checkbox "Descuento". Se habilitarán los radios "%" y "RD$" para elegir el tipo, y un campo para ingresar el valor. El preview muestra el equivalente en el otro modo (si entras 10%, muestra -RD$80; si entras RD$80, muestra -10%). El descuento se aplica al precio de confección.',
      },
      {
        q: '¿Qué notificaciones se envían automáticamente?',
        a: 'Al alcanzar el 50% de pago: notificación a Admin y Taller (PRE_PRODUCCIÓN). Al marcar como Confeccionado: notificación al Vendedor. Al marcar Listo para Entrega: notificación al Cliente.',
      },
      {
        q: '¿Cómo abonar al saldo de un pedido?',
        a: 'Desde el detalle del pedido, usa el botón "Registrar Pago" o "Facturar" para procesar pagos adicionales. Cada pago actualiza el saldo y puede triggear cambios de estado automáticos.',
      },
      {
        q: '¿Puedo agregar servicios adicionales a un pedido?',
        a: 'Sí. Al configurar cada prenda, puedes agregar servicios como botones, cremalleras, bordados, etc. Cada servicio tiene un precio y se suma al total de la prenda.',
      },
    ],
  },
  {
    id: 'service-orders',
    icon: Wrench,
    title: 'Órdenes de Servicio',
    roles: ['admin', 'vendedor'],
    color: 'text-orange-500',
    items: [
      {
        q: '¿Qué es una orden de servicio?',
        a: 'Una orden de servicio es para trabajos de reparación o alteración de prendas que ya tiene el cliente. Se diferencia de los pedidos de confección en que no implica fabricar una prenda nueva.',
      },
      {
        q: '¿Cómo crear una orden de servicio?',
        a: 'Ve a "Orden Servicios" y haz clic en "+" o "Nueva Orden". Selecciona el cliente, describe el servicio, define el precio y el anticipo. Al guardar, el sistema te lleva al POS para cobrar.',
      },
      {
        q: '¿Cuál es el anticipo mínimo?',
        a: 'El anticipo mínimo es el 15% del total. Puedes usar los botones de atajo (15%, 50%, 100%) o ingresar un monto personalizado.',
      },
    ],
  },
  {
    id: 'cotizaciones',
    icon: FileText,
    title: 'Cotizaciones',
    roles: ['admin', 'vendedor'],
    color: 'text-teal-500',
    items: [
      {
        q: '¿Cómo crear una cotización?',
        a: 'Ve a "Cotizaciones" y haz clic en "Nueva Cotización". Agrega los productos, cantidades y aplica descuentos si corresponde. Puedes enviarla por correo al cliente o imprimirla.',
      },
      {
        q: '¿Se puede convertir una cotización en factura?',
        a: 'Sí. Desde el detalle de la cotización encontrarás la opción para convertirla directamente en una factura en el POS.',
      },
    ],
  },
  {
    id: 'invoices',
    icon: FileSearch,
    title: 'Listado de Ventas',
    roles: ['admin', 'vendedor'],
    color: 'text-slate-500',
    items: [
      {
        q: '¿Cómo buscar una factura?',
        a: 'En "Listado Ventas" puedes filtrar por fecha, cliente o número de factura. Usa los selectores de fecha para acotar el rango.',
      },
      {
        q: '¿Puedo reenviar el comprobante de una factura?',
        a: 'Sí. Abre el detalle de la factura y usa el botón de envío por correo para reenviar el comprobante al cliente.',
      },
      {
        q: '¿Cómo imprimir una factura?',
        a: 'Desde el detalle de la factura encontrarás el botón "Imprimir" o "Descargar PDF".',
      },
    ],
  },

  // ── Solo Admin ──────────────────────────────────────────────────────────
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Dashboard',
    roles: ['admin'],
    color: 'text-cyan-500',
    items: [
      {
        q: '¿Qué información muestra el Dashboard?',
        a: 'Ventas del día, ventas del mes, pedidos activos (con alerta de vencidos), gráfica de ventas por semana, ventas por categoría del mes y alertas de stock bajo (bajo demanda).',
      },
      {
        q: '¿Cómo filtrar el Dashboard por sucursal?',
        a: 'En la esquina superior derecha del Dashboard encontrarás un selector de sucursal. Al cambiar la sucursal, todos los KPIs se actualizan. La selección se guarda para tu próxima visita.',
      },
      {
        q: '¿Cómo ver las alertas de stock bajo?',
        a: 'Las alertas de stock no se cargan automáticamente para no ralentizar la página. Haz clic en "Consultar" en la tarjeta de Alertas de Stock. Puedes actualizarlas en cualquier momento con el botón "↻ Actualizar".',
      },
    ],
  },
  {
    id: 'productos',
    icon: Package,
    title: 'Productos',
    roles: ['admin'],
    color: 'text-emerald-500',
    items: [
      {
        q: '¿Cómo crear un producto?',
        a: 'Ve a "Productos" y haz clic en "Nuevo Producto". Completa nombre, categoría, precio y agrega las variantes de talla y color. Puedes seleccionar colores del catálogo o crear nuevos directamente desde el campo.',
      },
      {
        q: '¿Cómo agregar un color nuevo?',
        a: 'En el campo de color del producto, escribe el nombre del nuevo color y selecciona "Agregar como nuevo". El color se guarda en el catálogo y queda disponible para todos los productos. Los colores se muestran con la primera letra en mayúscula.',
      },
      {
        q: '¿Cómo crear una categoría nueva?',
        a: 'En el campo de categoría, escribe el nombre de la nueva categoría y selecciona "Crear como nueva". Quedará disponible para todos los productos.',
      },
      {
        q: '¿Qué es un producto de tipo "Servicio"?',
        a: 'Los productos marcados como servicio aparecen en las órdenes de servicio y como servicios adicionales en pedidos de confección. No tienen variantes de talla/color.',
      },
      {
        q: '¿Cómo defino el stock mínimo de un producto?',
        a: 'En la configuración de cada variante (talla/color) puedes definir el stock mínimo. Cuando el stock actual esté por debajo de ese valor, aparecerá en las alertas del Dashboard.',
      },
    ],
  },
  {
    id: 'inventario',
    icon: Warehouse,
    title: 'Inventario por Sucursal',
    roles: ['admin'],
    color: 'text-blue-500',
    items: [
      {
        q: '¿Cómo ver el inventario?',
        a: 'Ve a "Inventario Sucursal". Por defecto muestra los 5 productos más vendidos. Usa el buscador para encontrar productos específicos o haz clic en "Ver todo el inventario".',
      },
      {
        q: '¿Cómo agregar stock?',
        a: 'Haz clic en "Agregar Stock". Busca el producto en el combobox (muestra los más usados por defecto), selecciona la variante, ingresa la cantidad y confirma.',
      },
      {
        q: '¿Cómo transferir stock entre sucursales?',
        a: 'Haz clic en "Transferir". Selecciona el producto de origen, la sucursal destino y la cantidad. La transferencia se registra en el historial de inventario.',
      },
    ],
  },
  {
    id: 'reportes',
    icon: FileCheck,
    title: 'Reportes',
    roles: ['admin'],
    color: 'text-violet-500',
    items: [
      {
        q: '¿Qué información muestran los reportes?',
        a: 'Total de ventas, ticket promedio, facturas emitidas, ventas por día (gráfica de barras), métodos de pago (gráfica donut) y ventas por sucursal.',
      },
      {
        q: '¿Cómo filtrar los reportes?',
        a: 'Usa los presets rápidos (Hoy, Semana, Mes, Año) o selecciona un rango de fechas personalizado. Si eres admin, también puedes filtrar por sucursal.',
      },
      {
        q: '¿Cómo exportar los datos?',
        a: 'Haz clic en "Exportar Excel". Se descargará un archivo .xlsx con todos los datos del período filtrado.',
      },
    ],
  },
  {
    id: 'notas-credito',
    icon: CreditCard,
    title: 'Notas de Crédito',
    roles: ['admin'],
    color: 'text-rose-500',
    items: [
      {
        q: '¿Cuándo se usa una nota de crédito?',
        a: 'Las notas de crédito se emiten cuando necesitas anular parcial o totalmente una factura ya emitida, o cuando hay una devolución de mercancía.',
      },
      {
        q: '¿Cómo crear una nota de crédito?',
        a: 'Ve a "Notas de Crédito" y selecciona la factura a anular. Indica el motivo y el monto a acreditar.',
      },
    ],
  },
  {
    id: 'sucursales',
    icon: Building2,
    title: 'Sucursales',
    roles: ['admin'],
    color: 'text-slate-500',
    items: [
      {
        q: '¿Cómo crear una sucursal?',
        a: 'Ve a "Sucursales" y haz clic en "Nueva Sucursal". Completa nombre, dirección y teléfono.',
      },
      {
        q: '¿Cómo asignar un vendedor a una sucursal?',
        a: 'Desde el módulo de Usuarios, edita el usuario y selecciona la sucursal en el campo correspondiente.',
      },
    ],
  },
  {
    id: 'usuarios',
    icon: UserCog,
    title: 'Usuarios',
    roles: ['admin'],
    color: 'text-slate-500',
    items: [
      {
        q: '¿Qué roles existen?',
        a: 'Admin: acceso completo a todas las sucursales y configuración. Vendedor/Cajero: acceso limitado a su sucursal asignada, sin acceso a reportes, productos, usuarios ni configuración general.',
      },
      {
        q: '¿Cómo crear un usuario?',
        a: 'Ve a "Usuarios" y haz clic en "Nuevo Usuario". Completa nombre, correo, contraseña, rol y sucursal. El usuario podrá acceder inmediatamente.',
      },
      {
        q: '¿Cómo cambiar la contraseña de un usuario?',
        a: 'Edita el usuario y usa la sección "Cambiar contraseña" al final del formulario.',
      },
      {
        q: '¿Qué es "Requiere Caja"?',
        a: 'Cuando está activo, el usuario debe abrir un turno de caja antes de poder facturar o crear pedidos. Los admin siempre están exentos. Esta opción no aplica para usuarios admin.',
      },
    ],
  },
  {
    id: 'configuracion',
    icon: Settings,
    title: 'Configuración',
    roles: ['admin'],
    color: 'text-slate-500',
    items: [
      {
        q: '¿Qué se puede configurar en la sección Empresa?',
        a: 'Nombre de la empresa, RNC, dirección, teléfono y logo que aparecerán en los comprobantes.',
      },
      {
        q: '¿Cómo configurar el recargo por tarjeta?',
        a: 'En Configuración → Impresión, activa el recargo por tarjeta e ingresa el porcentaje. El sistema mostrará automáticamente los precios con y sin recargo en el POS y en pedidos.',
      },
      {
        q: '¿Cómo habilitar el campo de envío en el POS?',
        a: 'En Configuración → Impresión, activa "Habilitar campo de envío en POS". Esto mostrará un campo de monto de envío en la pantalla de facturación.',
      },
      {
        q: '¿Cómo configurar los NCF (Número de Comprobante Fiscal)?',
        a: 'En Configuración → NCF, ingresa los rangos de comprobantes según la autorización de la DGII. El sistema los asignará automáticamente al facturar.',
      },
      {
        q: '¿Cómo cambiar mi contraseña?',
        a: 'Ve a Configuración → Mis Preferencias. Expande la sección "Cambiar contraseña", ingresa la contraseña actual y la nueva, y guarda.',
      },
      {
        q: '¿Qué son las preferencias de comprobante?',
        a: '"Imprimir" abre el diálogo de impresión automáticamente. "Correo" envía el comprobante al cliente por correo. "Preguntar" muestra un modal para elegir cada vez.',
      },
    ],
  },
];

// ── Componente ────────────────────────────────────────────────────────────────
const Manual = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [search, setSearch] = useState('');

  const sections = useMemo(() => {
    const roleSections = ALL_SECTIONS.filter(s => s.roles.includes(user?.role || 'vendedor'));
    if (!search.trim()) return roleSections;
    const q = search.toLowerCase();
    return roleSections
      .map(s => ({
        ...s,
        items: s.items.filter(i => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q)),
      }))
      .filter(s => s.items.length > 0 || s.title.toLowerCase().includes(q));
  }, [search, user?.role]);

  const totalItems = useMemo(() => sections.reduce((n, s) => n + s.items.length, 0), [sections]);

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="manual-page">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
            <BookOpen className="w-8 h-8 text-cyan-500" />
            Manual de Usuario
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin ? 'Guía completa del sistema' : 'Guía para tu rol de vendedor'}
            {' · '}
            <Badge variant="outline" className="text-xs capitalize">{user?.role}</Badge>
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium transition-colors print:hidden"
        >
          <Printer className="w-4 h-4" />
          Imprimir / Guardar PDF
        </button>
      </div>

      {/* Buscador */}
      <div className="relative print:hidden">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar en el manual..."
          className="pl-9"
        />
        {search && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
            {totalItems} resultado{totalItems !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Índice de módulos */}
      {!search && (
        <div className="flex flex-wrap gap-2 print:hidden">
          {sections.map(s => {
            const Icon = s.icon;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-xs text-slate-600 hover:border-cyan-400 hover:text-cyan-600 transition-colors"
              >
                <Icon className={`w-3.5 h-3.5 ${s.color}`} />
                {s.title}
              </a>
            );
          })}
        </div>
      )}

      {/* Secciones */}
      {sections.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">
          No se encontraron resultados para "{search}"
        </div>
      ) : (
        sections.map(section => {
          const Icon = section.icon;
          return (
            <div key={section.id} id={section.id} className="scroll-mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon className={`w-5 h-5 ${section.color}`} />
                <h2 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Outfit' }}>
                  {section.title}
                </h2>
                {section.roles.length === 1 && section.roles[0] === 'admin' && (
                  <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200">Solo Admin</Badge>
                )}
              </div>
              <Accordion type="multiple" className="border border-slate-200 rounded-xl overflow-hidden">
                {section.items.map((item, i) => (
                  <AccordionItem key={i} value={`${section.id}-${i}`} className="border-b border-slate-100 last:border-0">
                    <AccordionTrigger className="px-4 py-3 text-sm font-medium text-slate-700 hover:text-cyan-600 hover:no-underline text-left">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          );
        })
      )}

      {/* Footer */}
      <div className="text-center text-xs text-slate-400 pt-4 border-t border-slate-100 print:pt-8">
        Batas Médicas RD · v{process.env.REACT_APP_VERSION} · Manual generado para el rol: <span className="capitalize">{user?.role}</span>
      </div>

      {/* Estilos de impresión */}
      <style>{`
        @media print {
          body { font-size: 12px; }
          .print\\:hidden { display: none !important; }
          [data-radix-accordion-content] { display: block !important; height: auto !important; }
        }
      `}</style>
    </div>
  );
};

export default Manual;
